/**
 * 암호화 동기화 엔진 (SYNC_SECURITY §3·§8·§9·§10, 계획서 §7).
 *
 * 원격 구조 (Drive appDataFolder, 평면 이름공간):
 *   vault.json                 — 포장된 키만 (평문 상담데이터 없음)
 *   device-{deviceId}.sync     — 각 기기가 자기 파일에만 쓰는 암호화 bundle (쓰기 경합 없음)
 *   blob-{blobId}.sync         — 원문 전사 chunk (불변)
 *
 * 병합 규칙 (git과 같은 조상 판정; 시계에 의존하지 않음):
 *   각 기록 버전의 지문 = 암호문 해시. bundle은 기록마다 "이 버전이 거쳐 온 지문 목록"을 함께 보낸다.
 *   - 같은 지문            → 같다
 *   - 원격이 내 버전을 포함 → 원격이 최신: 받아들인다 (단, 승인 기록 보호 규칙 통과 시)
 *   - 내 버전이 원격을 포함 → 내가 최신: 유지
 *   - 서로 포함하지 않음    → 공통 조상 기준으로 다른 필드끼리면 자동 병합, 같은 필드·승인 기록이면 SyncConflict
 * 어떤 경우에도 조용히 덮어쓰지 않고, 로컬 데이터는 실패 시 그대로 남는다.
 */
import { DATA_FORMAT_REVISION, SCHEMA_VERSION, type AnyEntity, type EntityType } from '@/domain/schema/v1';
import { changedContentFields, checkWrite, isFrozen } from '@/domain/rules/writeRules';
import {
  envelopeFromJson,
  envelopeToJson,
  open,
  openJson,
  sealJson,
  type Envelope,
  type EnvelopeJson,
} from '../crypto/envelope';
import { bytesEqual, toBase64 } from '../crypto/encoding';
import type { DataKey } from '../crypto/envelope';
import { isVaultHeader, type VaultHeader } from '../crypto/vault';
import { META, type BlobChunk, type CounselDb, type StoredRecord } from '../db/schema';
import type { Repository } from '../repo/repository';
import type { RemoteStore } from './remote';

export const VAULT_FILE = 'vault.json';
export const DEVICE_PREFIX = 'device-';
export const BLOB_PREFIX = 'blob-';
const ANCESTRY_LIMIT = 400;
/** 기기별 작업 상태: 동기화하지 않는다 (충돌 기록이 다시 충돌을 만들지 않게) */
const DEVICE_LOCAL_TYPES = new Set<EntityType>(['SyncConflict', 'SyncState', 'AuditEvent']);

/**
 * 동기화 단위(part): 회기에 속한 기록은 회기별, 내담자 단위 기록은 내담자별, 나머지는 공통.
 * 바뀐 part의 파일만 올리고, 바뀐 파일만 받는다 (전사 정리본이 쌓여도 매번 전체를 올리지 않음).
 */
export function partOf(r: Pick<StoredRecord, 'clientId' | 'sessionId'>): string {
  return r.sessionId ? `s-${r.sessionId}` : r.clientId ? `c-${r.clientId}` : 'g';
}
const partFile = (deviceId: string, part: string) => `${DEVICE_PREFIX}${deviceId}--${part}.sync`;
function parsePartFile(name: string): { deviceId: string; part: string } | null {
  const m = /^device-(.+)--(g|[cs]-[0-9a-f-]+).sync$/.exec(name);
  return m ? { deviceId: m[1]!, part: m[2]! } : null;
}

export class VaultMismatchError extends Error {
  constructor() {
    super('Drive에 다른 vault의 데이터가 있습니다. 같은 passphrase로 만든 vault끼리만 동기화할 수 있습니다.');
    this.name = 'VaultMismatchError';
  }
}

export class NewerRemoteError extends Error {
  constructor() {
    super('다른 기기가 더 새로운 앱 버전의 데이터를 올렸습니다. 이 기기의 앱을 업데이트한 뒤 동기화하세요.');
    this.name = 'NewerRemoteError';
  }
}

export type SyncCtx = {
  db: CounselDb;
  dataKey: DataKey;
  vaultId: string;
  deviceId: string;
  repo: Repository;
  remote: RemoteStore;
  deviceLabel?: string;
};

export type SyncReport = {
  devicesRead: number;
  inserted: number;
  fastForwarded: number;
  merged: number;
  conflicts: number;
  pushed: boolean;
  partsPushed?: number;
  blobsUp: number;
  blobsDown: number;
  headerAdopted: boolean;
};

type RowJson = Omit<StoredRecord, 'env'> & { env: EnvelopeJson };
type BundleRecord = { row: RowJson; anc: string[] };
type BundleBody = { deviceLabel?: string; records: BundleRecord[] };
type BundleFile = {
  format: 'counselai-sync';
  v: 2;
  vaultId: string;
  deviceId: string;
  part: string;
  writtenAt: string;
  dataSchemaVersion: number;
  /** DATA_FORMAT_REVISION (없으면 0) */
  formatRevision?: number;
  body: EnvelopeJson;
};

const bundleAad = (vaultId: string, deviceId: string, part: string, writtenAt: string) =>
  `counselai|sync2|${vaultId}|${deviceId}|${part}|${writtenAt}`;
const blobAad = (vaultId: string, blobId: string, chunk: number, total: number) =>
  `counselai|blob|${vaultId}|${blobId}|${chunk}|${total}`;

/** 버전 지문: IV+암호문의 SHA-256 앞 128bit */
export async function rowHash(env: Envelope): Promise<string> {
  const buf = new Uint8Array(env.iv.length + env.ct.length);
  buf.set(env.iv, 0);
  buf.set(env.ct, env.iv.length);
  const d = new Uint8Array(await crypto.subtle.digest('SHA-256', buf as BufferSource));
  return toBase64(d.subarray(0, 16)).replace(/=+$/, '');
}

const toRowJson = (r: StoredRecord): RowJson => ({ ...r, env: envelopeToJson(r.env) });
const fromRowJson = (r: RowJson): StoredRecord => ({ ...r, env: envelopeFromJson(r.env) });

/** 이 기록이 이 기기에서 거쳐 온 모든 버전의 지문 (history + 동기화로 알게 된 지문) */
async function knownHashes(
  db: CounselDb,
  type: EntityType,
  id: string,
): Promise<{ ordered: string[]; set: Set<string> }> {
  const hist = await db.history.where('[entityType+id]').equals([type, id]).sortBy('revision');
  const anc = await db.ancestry.where('[entityType+id]').equals([type, id]).toArray();
  const pairs: [number, string][] = [];
  for (const h of hist) pairs.push([h.revision, await rowHash(h.env)]);
  for (const a of anc) pairs.push([a.revision, a.hash]);
  pairs.sort((x, y) => x[0] - y[0]);
  const ordered = [...new Set(pairs.map((p) => p[1]))];
  return { ordered, set: new Set(ordered) };
}

export class SyncEngine {
  private report!: SyncReport;

  constructor(private readonly ctx: SyncCtx) {}

  async sync(): Promise<SyncReport> {
    this.report = {
      devicesRead: 0,
      inserted: 0,
      fastForwarded: 0,
      merged: 0,
      conflicts: 0,
      pushed: false,
      blobsUp: 0,
      blobsDown: 0,
      headerAdopted: false,
    };
    const { db, remote, deviceId } = this.ctx;
    const startSeq = (await db.outbox.orderBy('seq').last())?.seq ?? 0;

    await this.syncVaultHeader();
    const dirty = new Set<string>();
    await this.pull(dirty);
    await this.syncBlobs();

    // 올릴 part: 이번 동기화 전까지 바뀐 기록의 part + 병합으로 바뀐 part + 아직 올린 적 없는 part
    const ownParts = new Set(
      (await remote.list(`${DEVICE_PREFIX}${deviceId}--`))
        .map((f) => parsePartFile(f.name)?.part)
        .filter((p): p is string => !!p),
    );
    for (const o of await db.outbox.where('seq').belowOrEqual(startSeq).toArray()) {
      if (DEVICE_LOCAL_TYPES.has(o.entityType)) continue;
      const row = await db.entities.get([o.entityType, o.id]);
      if (row) dirty.add(partOf(row));
    }
    const groups = new Map<string, StoredRecord[]>();
    for (const r of await db.entities.toArray()) {
      if (DEVICE_LOCAL_TYPES.has(r.entityType)) continue;
      const p = partOf(r);
      groups.set(p, [...(groups.get(p) ?? []), r]);
    }
    for (const p of groups.keys()) if (!ownParts.has(p)) dirty.add(p);
    for (const p of dirty) {
      const rows = groups.get(p);
      if (rows) await this.pushPart(p, rows);
    }
    // 이번에 반영한 시점까지의 outbox만 비운다 (동기화 중 새로 생긴 변경은 다음 번에)
    await db.outbox.where('seq').belowOrEqual(startSeq).delete();
    this.report.pushed = dirty.size > 0;
    this.report.partsPushed = [...dirty].filter((p) => groups.has(p)).length;
    await db.meta.put({
      key: META.syncStatus,
      value: { lastSyncAt: new Date().toISOString(), lastError: null, report: this.report },
    });
    return this.report;
  }

  // ---------------- vault 헤더 ----------------

  private async syncVaultHeader() {
    const { db, remote } = this.ctx;
    const local = (await db.meta.get(META.vault))?.value as VaultHeader;
    const raw = await remote.read(VAULT_FILE);
    if (!raw) {
      await remote.write(VAULT_FILE, JSON.stringify(local));
      return;
    }
    let remoteHeader: unknown;
    try {
      remoteHeader = JSON.parse(raw);
    } catch {
      throw new VaultMismatchError();
    }
    if (!isVaultHeader(remoteHeader) || remoteHeader.vaultId !== local.vaultId) throw new VaultMismatchError();
    const ru = remoteHeader.updatedAt ?? remoteHeader.createdAt;
    const lu = local.updatedAt ?? local.createdAt;
    if (ru > lu) {
      // 다른 기기에서 passphrase를 바꿨다: 같은 DEK의 새 포장을 채택
      await db.meta.put({ key: META.vault, value: remoteHeader });
      this.report.headerAdopted = true;
    } else if (lu > ru) {
      await remote.write(VAULT_FILE, JSON.stringify(local));
    }
  }

  // ---------------- 가져오기 ----------------

  private async pull(dirty: Set<string>): Promise<void> {
    const { db, remote, vaultId, deviceId } = this.ctx;
    const seen = ((await db.meta.get(META.syncSeen))?.value as Record<string, string> | undefined) ?? {};
    for (const f of await remote.list(DEVICE_PREFIX)) {
      const parsed = parsePartFile(f.name);
      if (!parsed || parsed.deviceId === deviceId) continue; // 이전 형식(v1) 파일·내 파일은 건너뜀
      if (seen[f.name] === f.modifiedTime) continue;
      const text = await remote.read(f.name);
      if (!text) continue;
      const file = JSON.parse(text) as BundleFile;
      if (file.format !== 'counselai-sync' || file.vaultId !== vaultId) throw new VaultMismatchError();
      if (file.dataSchemaVersion > SCHEMA_VERSION || (file.formatRevision ?? 0) > DATA_FORMAT_REVISION) throw new NewerRemoteError();
      if (file.v !== 2 || file.part !== parsed.part || file.deviceId !== parsed.deviceId) continue;
      const body = await openJson<BundleBody>(
        this.ctx.dataKey,
        envelopeFromJson(file.body),
        bundleAad(vaultId, file.deviceId, file.part, file.writtenAt),
      );
      for (const rec of body.records) {
        if (DEVICE_LOCAL_TYPES.has(rec.row.entityType)) continue;
        const row = fromRowJson(rec.row);
        if (partOf(row) !== file.part) continue; // 파일과 맞지 않는 기록은 받지 않음
        if (await this.mergeRecord(row, rec.anc, file.deviceId)) dirty.add(file.part);
      }
      seen[f.name] = f.modifiedTime;
      this.report.devicesRead++;
    }
    await db.meta.put({ key: META.syncSeen, value: seen });
  }

  /** @returns 로컬이 바뀌었는지 (병합·충돌 해결로 새 버전을 만든 경우 다시 올려야 함) */
  private async mergeRecord(remoteRow: StoredRecord, remoteAnc: string[], remoteDeviceId: string): Promise<boolean> {
    const { db, repo } = this.ctx;
    const type = remoteRow.entityType;
    const id = remoteRow.id;
    // 원격 행은 이 vault 키로 열리고 스키마를 통과해야만 받아들인다
    const remoteEntity = (await repo.decryptRecord(remoteRow)) as AnyEntity;
    const rh = await rowHash(remoteRow.env);

    const local = await db.entities.get([type, id]);
    if (!local) {
      await this.putReplica(remoteRow, remoteAnc, null);
      this.report.inserted++;
      return false;
    }
    const lh = await rowHash(local.env);
    if (lh === rh) return false;

    const remoteKnows = new Set(remoteAnc);
    const known = await knownHashes(db, type, id);

    if (remoteKnows.has(lh)) {
      // 원격이 내 버전 이후의 버전: 받아들이되 승인 기록 보호 규칙은 그대로 적용
      const localEntity = (await repo.decryptRecord(local)) as AnyEntity;
      if (checkWrite(localEntity, remoteEntity, 'user').length > 0) {
        await this.recordConflict(localEntity, remoteEntity, lh, rh, remoteDeviceId, 'invalid-change');
        return false;
      }
      await this.putReplica(remoteRow, remoteAnc, local);
      await this.autoResolveConflicts(type, id, new Set([...remoteAnc, rh]));
      this.report.fastForwarded++;
      return false;
    }
    if (known.set.has(rh)) return false; // 내가 더 최신

    // 갈라짐: 공통 조상 찾기
    const localEntity = (await repo.decryptRecord(local)) as AnyEntity;
    let baseHash: string | undefined;
    for (let i = remoteAnc.length - 1; i >= 0; i--) {
      if (known.set.has(remoteAnc[i]!)) {
        baseHash = remoteAnc[i];
        break;
      }
    }
    if (await this.hasOpenConflict(type, id, rh)) return false;
    if (isFrozen(localEntity) || isFrozen(remoteEntity)) {
      await this.recordConflict(localEntity, remoteEntity, lh, rh, remoteDeviceId, 'approved-record');
      return false;
    }
    const base = baseHash ? await this.findHistoryByHash(type, id, baseHash) : null;
    if (!base) {
      await this.recordConflict(localEntity, remoteEntity, lh, rh, remoteDeviceId, 'no-common-base');
      return false;
    }
    const mine = changedContentFields(base, localEntity);
    const theirs = changedContentFields(base, remoteEntity);
    if (mine.some((f) => theirs.includes(f))) {
      await this.recordConflict(localEntity, remoteEntity, lh, rh, remoteDeviceId, 'same-field');
      return false;
    }
    // 서로 다른 필드: 자동 병합 (SYNC_SECURITY §9)
    const merged: Record<string, unknown> = { ...(localEntity as Record<string, unknown>) };
    for (const f of theirs) {
      const v = (remoteEntity as Record<string, unknown>)[f];
      if (v === undefined) delete merged[f];
      else merged[f] = v;
    }
    if (remoteEntity.deletedAt !== localEntity.deletedAt && base.deletedAt === localEntity.deletedAt) {
      merged.deletedAt = remoteEntity.deletedAt;
    }
    await this.rememberAncestry(type, id, [...remoteAnc, rh], remoteRow.revision);
    await repo.commitVersion(localEntity, merged, Math.max(local.revision, remoteRow.revision) + 1, 'system');
    this.report.merged++;
    return true;
  }

  /** 원격 행을 그대로(암호문 그대로) 로컬에 반영. 그 사이 로컬이 바뀌었으면 반영하지 않는다. */
  private async putReplica(row: StoredRecord, anc: string[], expectedLocal: StoredRecord | null) {
    const { db } = this.ctx;
    await db.transaction('rw', db.entities, db.history, db.ancestry, async () => {
      const cur = await db.entities.get([row.entityType, row.id]);
      if ((cur?.revision ?? null) !== (expectedLocal?.revision ?? null)) return; // 다음 동기화에서 다시 판정
      if (cur && expectedLocal && !bytesEqual(cur.env.ct, expectedLocal.env.ct)) return;
      await db.entities.put(row);
      if (!(await db.history.get([row.entityType, row.id, row.revision]))) await db.history.put(row);
      await db.ancestry.bulkPut(
        anc.slice(-ANCESTRY_LIMIT).map((hash) => ({ entityType: row.entityType, id: row.id, hash, revision: 0 })),
      );
    });
  }

  private async rememberAncestry(type: EntityType, id: string, hashes: string[], revision: number) {
    await this.ctx.db.ancestry.bulkPut(
      hashes.slice(-ANCESTRY_LIMIT).map((hash) => ({ entityType: type, id, hash, revision })),
    );
  }

  private async findHistoryByHash(type: EntityType, id: string, hash: string): Promise<AnyEntity | null> {
    const rows = await this.ctx.db.history.where('[entityType+id]').equals([type, id]).toArray();
    for (const r of rows) {
      if ((await rowHash(r.env)) === hash) return (await this.ctx.repo.decryptRecord(r)) as AnyEntity;
    }
    return null;
  }

  private async hasOpenConflict(type: EntityType, id: string, remoteHash: string): Promise<boolean> {
    const open = await this.ctx.repo.list('SyncConflict');
    return open.some(
      (c) =>
        c.targetEntityType === type &&
        c.targetEntityId === id &&
        c.resolution === 'unresolved' &&
        c.remoteHash === remoteHash,
    );
  }

  private async recordConflict(
    localEntity: AnyEntity,
    remoteEntity: AnyEntity,
    localHash: string,
    remoteHash: string,
    remoteDeviceId: string,
    reason: 'same-field' | 'approved-record' | 'no-common-base' | 'invalid-change',
  ) {
    if (await this.hasOpenConflict(localEntity.entityType, localEntity.id, remoteHash)) return;
    await this.ctx.repo.create(
      'SyncConflict',
      {
        targetEntityType: localEntity.entityType,
        targetEntityId: localEntity.id,
        localRevision: localEntity.revision,
        remoteRevision: remoteEntity.revision,
        localPayload: localEntity,
        remotePayload: remoteEntity,
        localHash,
        remoteHash,
        remoteDeviceId,
        reason,
        resolution: 'unresolved',
      },
      'system',
    );
    this.report.conflicts++;
  }

  /** 다른 기기에서 이미 해결된 충돌은 자동으로 닫는다 (같은 충돌을 두 번 해결하지 않게) */
  private async autoResolveConflicts(type: EntityType, id: string, nowKnown: Set<string>) {
    const open = (await this.ctx.repo.list('SyncConflict')).filter(
      (c) => c.targetEntityType === type && c.targetEntityId === id && c.resolution === 'unresolved',
    );
    for (const c of open) {
      if (c.localHash && c.remoteHash && nowKnown.has(c.localHash) && nowKnown.has(c.remoteHash)) {
        await this.ctx.repo.update(
          'SyncConflict',
          c.id,
          { resolution: 'merged', resolvedAt: new Date().toISOString() },
          'system',
        );
      }
    }
  }

  // ---------------- 올리기 ----------------

  private async pushPart(part: string, rows: StoredRecord[]) {
    const { db, vaultId, deviceId } = this.ctx;
    const records: BundleRecord[] = [];
    for (const r of rows) {
      const { ordered } = await knownHashes(db, r.entityType, r.id);
      records.push({ row: toRowJson(r), anc: ordered.slice(-ANCESTRY_LIMIT) });
    }
    const writtenAt = new Date().toISOString();
    const body: BundleBody = { records, ...(this.ctx.deviceLabel ? { deviceLabel: this.ctx.deviceLabel } : {}) };
    const file: BundleFile = {
      format: 'counselai-sync',
      v: 2,
      vaultId,
      deviceId,
      part,
      writtenAt,
      dataSchemaVersion: ((await db.meta.get(META.dataSchemaVersion))?.value as number) ?? SCHEMA_VERSION,
      formatRevision: DATA_FORMAT_REVISION,
      body: envelopeToJson(await sealJson(this.ctx.dataKey, body, bundleAad(vaultId, deviceId, part, writtenAt))),
    };
    await this.ctx.remote.write(partFile(deviceId, part), JSON.stringify(file));
  }

  // ---------------- 원문 blob ----------------

  private async syncBlobs() {
    const { db, remote, vaultId, dataKey } = this.ctx;
    const remoteNames = new Set((await remote.list(BLOB_PREFIX)).map((f) => f.name));
    const localIds = new Set((await db.blobs.toArray()).map((b) => b.blobId));
    for (const blobId of localIds) {
      const name = `${BLOB_PREFIX}${blobId}.sync`;
      if (remoteNames.has(name)) continue;
      const chunks = (await db.blobs.where('blobId').equals(blobId).toArray()).sort((a, b) => a.chunk - b.chunk);
      await remote.write(
        name,
        JSON.stringify({ blobId, chunks: chunks.map((c) => ({ ...c, env: envelopeToJson(c.env) })) }),
      );
      this.report.blobsUp++;
    }
    for (const name of remoteNames) {
      const blobId = name.slice(BLOB_PREFIX.length, -'.sync'.length);
      if (localIds.has(blobId)) continue;
      const text = await remote.read(name);
      if (!text) continue;
      const parsed = JSON.parse(text) as { blobId: string; chunks: (Omit<BlobChunk, 'env'> & { env: EnvelopeJson })[] };
      const chunks: BlobChunk[] = parsed.chunks.map((c) => ({ ...c, env: envelopeFromJson(c.env) }));
      // 받기 전에 모든 chunk가 이 키로 열리는지 검증
      for (const c of chunks) await open(dataKey, c.env, blobAad(vaultId, blobId, c.chunk, c.total));
      if (chunks.length === 0 || chunks.length !== chunks[0]!.total) continue;
      await db.blobs.bulkPut(chunks);
      this.report.blobsDown++;
    }
  }
}
